import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Shield,
  Download,
  Trash2,
  Clock,
  FileText,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';

export function PrivacySection() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [retentionDays, setRetentionDays] = useState('365');

  // Fetch profile with privacy settings
  const { data: profile } = useQuery({
    queryKey: ['profile-privacy', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('data_retention_days, gdpr_accepted_at, ccpa_opted_out')
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Fetch data requests
  const { data: requests, isLoading: requestsLoading } = useQuery({
    queryKey: ['data-requests', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('data_deletion_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Update retention days
  const updateRetention = useMutation({
    mutationFn: async (days: number) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('profiles')
        .update({ data_retention_days: days })
        .eq('user_id', user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Data retention settings updated');
      queryClient.invalidateQueries({ queryKey: ['profile-privacy'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update settings');
    },
  });

  // Toggle CCPA opt-out
  const toggleCCPAOptOut = useMutation({
    mutationFn: async (optedOut: boolean) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('profiles')
        .update({ ccpa_opted_out: optedOut })
        .eq('user_id', user.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Privacy preference updated');
      queryClient.invalidateQueries({ queryKey: ['profile-privacy'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update preference');
    },
  });

  // Request data export
  const requestExport = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('data_deletion_requests')
        .insert({
          user_id: user.id,
          request_type: 'export',
          status: 'pending',
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Data export request submitted. We\'ll email you when ready.');
      queryClient.invalidateQueries({ queryKey: ['data-requests'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit request');
    },
  });

  // Request data deletion
  const requestDeletion = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('data_deletion_requests')
        .insert({
          user_id: user.id,
          request_type: 'deletion',
          status: 'pending',
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Data deletion request submitted. We\'ll process this within 30 days.');
      queryClient.invalidateQueries({ queryKey: ['data-requests'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to submit request');
    },
  });

  const getRequestStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500">Processing</Badge>;
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* GDPR Rights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Your Privacy Rights
          </CardTitle>
          <CardDescription>
            Manage your data according to GDPR and CCPA regulations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Data Export */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-500/10 shrink-0">
                <Download className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <h4 className="font-medium">Export Your Data</h4>
                <p className="text-sm text-muted-foreground">
                  Download all your personal data in a portable format
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => requestExport.mutate()}
              disabled={requestExport.isPending}
              className="w-full sm:w-auto shrink-0"
            >
              {requestExport.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Request Export
            </Button>
          </div>

          {/* Data Deletion */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/10 shrink-0">
                <Trash2 className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h4 className="font-medium">Delete Your Data</h4>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data
                </p>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full sm:w-auto shrink-0">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Request Deletion
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your
                    account and all associated data including QR codes, analytics,
                    and settings.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => requestDeletion.mutate()}
                  >
                    Yes, delete my data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <Separator />

          {/* CCPA Opt-out */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Do Not Sell My Personal Information</h4>
              <p className="text-sm text-muted-foreground">
                Opt out of any sale of your personal information (CCPA)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Note: We do not sell personal information, but you can still opt out.
              </p>
            </div>
            <Switch
              checked={profile?.ccpa_opted_out || false}
              onCheckedChange={(checked) => toggleCCPAOptOut.mutate(checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Data Retention */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Data Retention
          </CardTitle>
          <CardDescription>
            Configure how long we keep your analytics data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Retention Period (days)</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
                min="30"
                max="730"
                className="w-32"
              />
              <Button
                variant="outline"
                onClick={() => updateRetention.mutate(parseInt(retentionDays))}
                disabled={updateRetention.isPending}
              >
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Scan analytics older than this will be automatically deleted.
              Minimum 30 days, maximum 730 days (2 years).
            </p>
          </div>

          {profile?.data_retention_days && (
            <div className="p-3 rounded-lg bg-muted/50 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm">
                Currently retaining data for {profile.data_retention_days} days
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Request History
          </CardTitle>
          <CardDescription>
            Track your data export and deletion requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requestsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : requests?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No requests yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests?.map((request) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    {request.request_type === 'export' ? (
                      <Download className="h-4 w-4 text-blue-500" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-red-500" />
                    )}
                    <div>
                      <p className="font-medium capitalize">
                        Data {request.request_type}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(request.created_at), 'PPP')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRequestStatusBadge(request.status)}
                    {request.download_url && request.status === 'completed' && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={request.download_url} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
